/**
 * AdminHubPage — Central admin page with tile layout for all system items.
 * Replaces the SYSTEM sidebar group. Only visible to administrator/ops_manager.
 *
 * @route /admin
 * @since v2.305.0
 */
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import {
  IconUsers, IconSegment, IconEinstellungen, IconFeiertage, IconStandorte, IconRohdaten, IconApi,
} from '../components/Icons';
import { Mail, Upload, Activity, Code, BookOpen, ClipboardList, Settings, Bell, Sparkles, MessageSquare, Shield, ListChecks, Plug } from 'lucide-react';

const lc20 = (LucideIcon) => (p) => <LucideIcon width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" {...p} />;

const TILES = [
  { path: '/admin/users', labelKey: 'nav.user_management', descKey: 'adminHub.desc.users', Icon: IconUsers, modul: 'benutzerverwaltung' },
  { path: '/admin/segmente', labelKey: 'nav.customer_segments', descKey: 'adminHub.desc.segmente', Icon: IconSegment, modul: 'admin' },
  { path: '/admin/email', labelKey: 'nav.email_management', descKey: 'adminHub.desc.email', Icon: lc20(Mail), modul: 'admin' },
  { path: '/admin/sms-templates', labelKey: 'nav.sms_templates', descKey: 'adminHub.desc.smsTemplates', Icon: lc20(MessageSquare), modul: 'admin' },
  { path: '/admin/notification-events', labelKey: 'nav.notification_events', descKey: 'adminHub.desc.notificationEvents', Icon: lc20(Bell), modul: 'admin' },
  { path: '/admin/rollen', labelKey: 'nav.role_permissions', descKey: 'adminHub.desc.rollen', Icon: lc20(Shield), modul: 'admin' },
  { path: '/admin/szenarien', labelKey: 'nav.scenarios', descKey: 'adminHub.desc.szenarien', Icon: lc20(Bell), modul: 'admin' },
  { path: '/admin/notification-log', labelKey: 'nav.notification_log', descKey: 'adminHub.desc.notificationLog', Icon: lc20(ListChecks), modul: 'admin' },
  { path: '/admin/freshdesk-analyse', labelKey: 'nav.freshdesk_analyse', descKey: 'adminHub.desc.freshdeskAnalyse', Icon: lc20(Sparkles), modul: 'admin' },
  { path: '/admin/integrationen', labelKey: 'nav.integrationen', descKey: 'adminHub.desc.integrationen', Icon: lc20(Plug), modul: 'admin' },
  { path: '/einstellungen', labelKey: 'nav.settings', descKey: 'adminHub.desc.einstellungen', Icon: IconEinstellungen, modul: 'einstellungen' },
  { path: '/feiertage', labelKey: 'nav.public_holidays', descKey: 'adminHub.desc.feiertage', Icon: IconFeiertage, modul: 'admin' },
  { path: '/standorte', labelKey: 'nav.locations', descKey: 'adminHub.desc.standorte', Icon: IconStandorte, modul: 'admin' },
  { path: '/rohdaten', labelKey: 'nav.raw_data', descKey: 'adminHub.desc.rohdaten', Icon: IconRohdaten, modul: 'admin' },
  { path: '/data-hub', labelKey: 'nav.data_hub', descKey: 'adminHub.desc.datahub', Icon: IconApi, modul: 'admin' },
  { path: '/entwicklung/system-pulse', labelKey: 'nav.system_pulse', descKey: 'adminHub.desc.pulse', Icon: lc20(Activity), modul: 'admin' },
  { path: '/admin/deployment', labelKey: 'nav.deployment', descKey: 'adminHub.desc.deployment', Icon: lc20(Upload), modul: 'deployment', devOnly: true },
  { path: '/handbuch', labelKey: 'nav.manual', descKey: 'adminHub.desc.handbuch', Icon: lc20(BookOpen), modul: 'admin' },
  { path: '/dev-handbuch', labelKey: 'nav.dev_handbook', descKey: 'adminHub.desc.devhandbuch', Icon: lc20(Code), modul: 'admin' },
  { path: '/changelog', labelKey: 'nav.changelog', descKey: 'adminHub.desc.changelog', Icon: lc20(ClipboardList), modul: 'admin' },
];

export default function AdminHubPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const permissions = user?.permissions || {};
  const isDev = window.location.hostname.includes('dev.');

  const visibleTiles = TILES.filter(tile => {
    if (tile.devOnly && !isDev) return false;
    if (tile.modul) {
      const p = permissions[tile.modul];
      return p && p !== 'none';
    }
    return true;
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#64748B]/10 flex items-center justify-center">
          <Settings size={20} className="text-[#64748B]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('adminHub.title')}</h1>
          <p className="text-sm text-[var(--text-tertiary)]">{t('adminHub.subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleTiles.map(tile => (
          <button
            key={tile.path}
            onClick={() => navigate(tile.path)}
            className="flex items-start gap-4 p-5 rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] hover:border-[var(--primary,#338edc)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-all text-left group"
          >
            <div className="w-10 h-10 rounded-lg bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center flex-shrink-0 text-[var(--text-secondary)] group-hover:text-[var(--primary,#338edc)] transition-colors">
              <tile.Icon />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[var(--text-primary)] mb-0.5">{t(tile.labelKey)}</div>
              <div className="text-xs text-[var(--text-tertiary)] leading-relaxed">{t(tile.descKey)}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
